import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import {onMounted, ref} from "vue"
import {useI18n} from "vue3-i18n"
import {useOrganizationService} from "@/inversify.config"
import {notifyError, notifySuccess} from "@/notify"
import {getAuthenticatedMemberOrganizationRoute} from "@/router/helpers"
import {Invoice, OrganizationType, Role} from "@/types"
import {IFullOrganization} from "@/types/entities/organization";
import Card from "@/components/layout/Card.vue"
import MemberTable from "@/components/tables/MemberTable.vue"
import ContactTable from "@/components/tables/ContactTable.vue"
import {useMemberStore} from "@/stores/memberStore";
import {getFileFromUrl, getValueForLocale} from "@/helpers";
import OrganizationForm from "@/components/forms/OrganizationForm.vue";
import {IEditOwnOrganizationRequest} from "@/types/requests/editOwnOrganizationRequest";
import InvoiceTable from "@/components/tables/InvoiceTable.vue";

// eslint-disable-next-line

export default /*#__PURE__*/_defineComponent({
  __name: 'MemberOrganizationForm',
  props: {
    organizationId: {}
  },
  setup(__props: any) {

const props = __props

const {t, getLocale} = useI18n()
const memberStore = useMemberStore()
const organizationService = useOrganizationService()

const organizationLogo = ref<File>()
const organization = ref<IFullOrganization>()
const organizationInvoices = ref<Invoice[]>([]);
const organizationIsLoading = ref(false)

const addMemberPath = {
  name: `${getAuthenticatedMemberOrganizationRoute()}.children.addMember`,
  params: {organizationId: props.organizationId}
};
const addContactPath = {
  name: `${getAuthenticatedMemberOrganizationRoute()}.children.addContact`,
  params: {organizationId: props.organizationId}
};

onMounted(() => {
  loadOrganization()
})

async function loadOrganization() {
  organizationIsLoading.value = true
  let fullOrganization = await organizationService.getMyOrganization(getLocale())
  if (fullOrganization) {
    organization.value = fullOrganization
    organizationInvoices.value = getInvoices()
    if (fullOrganization.logo)
      organizationLogo.value = await getFileFromUrl(fullOrganization.logo, fullOrganization.logo)
  }
  organizationIsLoading.value = false
}

function getInvoices(): Invoice[] {
  if (!organization.value)
    return [];
  organization.value?.subscription.invoices?.forEach(x => {
    x.description = `${t(`products.membershipPlan`)} : ${x.createdAt}`
  })
  organization.value.memberInvoices.forEach(x => {
    let description = `${t(`activities.activityRegistration`)} ${getValueForLocale(x.productName)}`
    x.description = x.memberFullName ? `${description} ${t('global.table.of')} ${x.memberFullName}` : description
  })
  return organization.value?.subscription.invoices.concat(organization.value.memberInvoices);
}

async function onImageChange(file: File) {
  organizationLogo.value = file
}

async function handleSubmit(organization: IFullOrganization) {
  let request: IEditOwnOrganizationRequest = {
    type: organization.type,
    logo: organizationLogo.value,
    email: organization.email,
    phoneNumber: organization.phoneNumber,
    phoneExtension: organization.phoneExtension,
    apartment: organization.apartment,
    street: organization.street,
    city: organization.city,
    zipCode: organization.zipCode,
    enterpriseNumber: organization.enterpriseNumber,
    website: organization.website,
    numberOfUnits: organization.numberOfUnits ?? 0,
    yearsOfExperience: organization.yearsOfExperience,
    brokerType: organization.brokerType,
    activityAreas: organization.activityAreas,
    descriptionFr: organization.descriptionFr,
    descriptionEn: organization.descriptionEn
  };
  let succeededOrNotResponse = await organizationService.editMyOrganization(request)
  if (succeededOrNotResponse && succeededOrNotResponse.succeeded) {
    notifySuccess(t('validation.organization.update.success'))
  } else {
    let errorMessages = succeededOrNotResponse.getErrorMessages(
        'validation.organization.update', 'validation.organization.update.errorOccurred');
    notifyError(errorMessages[0])
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (organization.value)
      ? (_openBlock(), _createBlock(Card, {
          key: 0,
          title: organization.value.name
        }, {
          default: _withCtx(() => [
            _createVNode(OrganizationForm, {
              modelValue: organization.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((organization).value = $event)),
              "is-syndicate-organization": organization.value.type === _unref(OrganizationType).RegularSyndicate,
              onSubmit: handleSubmit,
              onOnImageChange: onImageChange
            }, null, 8, ["modelValue", "is-syndicate-organization"])
          ]),
          _: 1
        }, 8, ["title"]))
      : _createCommentVNode("", true),
    (organization.value && !_unref(memberStore).hasRole(_unref(Role).RegularSyndicateCoOwner))
      ? (_openBlock(), _createBlock(MemberTable, {
          key: 1,
          "card-link-path": addMemberPath,
          "is-loading": organizationIsLoading.value,
          members: organization.value.members,
          "organization-id": organization.value.id.toString()
        }, null, 8, ["is-loading", "members", "organization-id"]))
      : _createCommentVNode("", true),
    (organization.value && organization.value.type === _unref(OrganizationType).Corporate)
      ? (_openBlock(), _createBlock(ContactTable, {
          key: 2,
          "card-link-path": addContactPath,
          contacts: organization.value.contacts,
          "is-loading": organizationIsLoading.value,
          "organization-id": organization.value.id.toString()
        }, null, 8, ["contacts", "is-loading", "organization-id"]))
      : _createCommentVNode("", true),
    (organization.value && _unref(memberStore).hasRole(_unref(Role).RegularSyndicateAdmin))
      ? (_openBlock(), _createBlock(InvoiceTable, {
          key: 3,
          invoices: organizationInvoices.value,
          "is-loading": organizationIsLoading.value
        }, null, 8, ["invoices", "is-loading"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})